import { useCallback, useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import DataTableWithQuery from '@/components/ui/DataTableWithQuery';
import LocationTargetSelector from '@/components/ads/LocationTargetSelector';
import { userSelectionColumns } from '@/components/users/userSelectionColumns';
import { useAppUsers } from '@/hooks/useUsers';
import { AdCampaignLocationTarget } from '@/types/ads';
import {
  alertMessageService,
  type AlertTargetMode,
  type AlertMessage,
  type UpsertAlertMessageRequest,
} from '@/services/alertMessageService';

const AlertsTab = () => {
  const [alertMessages, setAlertMessages] = useState<AlertMessage[]>([]);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState<AlertMessage | null>(null);
  const [editorMessage, setEditorMessage] = useState('');
  const [targetMode, setTargetMode] = useState<AlertTargetMode>('ALL');
  const [targetUserIds, setTargetUserIds] = useState<string[]>([]);
  const [locationTargets, setLocationTargets] = useState<AdCampaignLocationTarget[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sortedMessages = useMemo(() => {
    return [...alertMessages].sort((a, b) => {
      if (a.createdDate && b.createdDate) {
        return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
      }
      return a.alert.localeCompare(b.alert);
    });
  }, [alertMessages]);

  const loadAlertMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const alerts = await alertMessageService.getAlertMessages();
      setAlertMessages(alerts);
    } catch (err) {
      console.error('Error fetching alert messages', err);
      setError(err instanceof Error ? err.message : 'Error al cargar los mensajes de alerta');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAlertMessages();
  }, [loadAlertMessages]);

  const resetEditor = () => {
    setEditingAlert(null);
    setEditorMessage('');
    setTargetMode('ALL');
    setTargetUserIds([]);
    setLocationTargets([]);
  };

  const openCreateModal = () => {
    resetEditor();
    setIsEditorOpen(true);
  };

  const openEditModal = (alert: AlertMessage) => {
    setEditingAlert(alert);
    setEditorMessage(alert.alert ?? '');
    setTargetMode(alert.targetMode ?? 'ALL');
    setTargetUserIds((alert.targetUserIds ?? []).map((id) => String(id)));
    setLocationTargets(alert.locationTargets ?? []);
    setIsEditorOpen(true);
  };

  const closeEditorModal = () => {
    setIsEditorOpen(false);
    resetEditor();
  };

  const buildPayload = (): UpsertAlertMessageRequest | null => {
    const alert = editorMessage.trim();
    if (!alert) {
      return null;
    }

    if (targetMode === 'USERS') {
      const selectedIds = targetUserIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      if (selectedIds.length === 0) {
        return null;
      }
      return {
        alert,
        targetMode: 'USERS',
        targetUserIds: selectedIds,
      };
    }

    if (targetMode === 'LOCATION') {
      if (locationTargets.length === 0) {
        return null;
      }
      return {
        alert,
        targetMode: 'LOCATION',
        locationTargets,
      };
    }

    return {
      alert,
      targetMode: 'ALL',
      targetUserIds: [],
      locationTargets: [],
    };
  };

  const handleSaveAlertMessage = async () => {
    const payload = buildPayload();
    if (!payload) return;

    try {
      setIsSaving(true);
      setError(null);

      if (editingAlert?.id) {
        const updatedAlert = await alertMessageService.updateAlertMessage(editingAlert.id, payload);
        setAlertMessages((prev) => prev.map((msg) => (msg.id === updatedAlert.id ? updatedAlert : msg)));
      } else {
        const createdAlert = await alertMessageService.createAlertMessage(payload);
        setAlertMessages((prev) => [...prev, createdAlert]);
      }

      closeEditorModal();
    } catch (err) {
      console.error('Error saving alert message', err);
      setError(err instanceof Error ? err.message : 'No se pudo guardar el mensaje de alerta');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAlertMessage = async (id: string) => {
    try {
      setDeletingId(id);
      setError(null);
      await alertMessageService.deleteAlertMessage(id);
      setAlertMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (err) {
      console.error('Error deleting alert message', err);
      setError(err instanceof Error ? err.message : 'No se pudo eliminar el mensaje de alerta');
    } finally {
      setDeletingId(null);
    }
  };

  const isSaveDisabled = useMemo(() => {
    if (!editorMessage.trim() || isSaving) {
      return true;
    }

    if (targetMode === 'USERS') {
      return targetUserIds.length === 0;
    }

    if (targetMode === 'LOCATION') {
      return locationTargets.length === 0;
    }

    return false;
  }, [editorMessage, isSaving, locationTargets.length, targetMode, targetUserIds.length]);

  const getTargetModeText = (message: AlertMessage) => {
    const mode = message.targetMode ?? 'ALL';
    if (mode === 'USERS') {
      return `Usuarios (${message.targetUserIds?.length ?? 0})`;
    }
    if (mode === 'LOCATION') {
      return `Ubicaciones (${message.locationTargets?.length ?? 0})`;
    }
    return 'Todos';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-800">Mensajes de alerta</h2>
        <Button onClick={openCreateModal}>
          <Plus size={16} className="mr-2" />
          Agregar mensaje
        </Button>
      </div>
      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mensaje
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Segmentación
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Editar
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Eliminar
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  Cargando mensajes de alerta...
                </td>
              </tr>
            )}

            {!isLoading && sortedMessages.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  No hay mensajes de alerta registrados.
                </td>
              </tr>
            )}

            {!isLoading &&
              sortedMessages.map((message, index) => (
                <tr key={message.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {message.alert}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {getTargetModeText(message)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(message)}
                      disabled={deletingId === message.id}
                    >
                      <Pencil size={14} />
                    </Button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void handleDeleteAlertMessage(message.id)}
                      disabled={deletingId === message.id}
                      loading={deletingId === message.id}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={isEditorOpen}
        onClose={closeEditorModal}
        title={editingAlert ? 'Editar mensaje de alerta' : 'Agregar mensaje de alerta'}
        size="xl"
      >
        <div className="space-y-4">
          <Input
            label="Mensaje de alerta"
            value={editorMessage}
            onChange={(e) => setEditorMessage(e.target.value)}
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Alcance</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {[
                { value: 'ALL', label: 'Todos' },
                { value: 'USERS', label: 'Usuarios específicos' },
                { value: 'LOCATION', label: 'Ubicación y Grupo' },
              ].map((option) => (
                <label
                  key={option.value}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                    targetMode === option.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-700'
                  }`}
                >
                  <input
                    type="radio"
                    name="targetMode"
                    value={option.value}
                    checked={targetMode === option.value}
                    onChange={() => {
                      setTargetMode(option.value as AlertTargetMode);
                      setTargetUserIds([]);
                      setLocationTargets([]);
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>

          {targetMode === 'ALL' && (
            <p className="text-sm text-gray-500">
              El mensaje de alerta se mostrará a todos los usuarios.
            </p>
          )}

          {targetMode === 'USERS' && (
            <div className="space-y-2">
              <div className="text-sm text-gray-500">
                Seleccionados: <span className="font-semibold text-gray-900">{targetUserIds.length}</span>
              </div>
              <DataTableWithQuery
                columns={userSelectionColumns}
                actions={[]}
                useQueryHook={useAppUsers}
                itemsPerPage={5}
                title=""
                searchPlaceholder="Buscar por nombre o usuario..."
                tableStyle="compact"
                enableRowSelection
                selectedRowIds={targetUserIds}
                onSelectionChange={setTargetUserIds}
                getRowId={(user) => String(user.id)}
                className="shadow-none border border-gray-200"
              />
            </div>
          )}

          {targetMode === 'LOCATION' && (
            <LocationTargetSelector
              value={locationTargets}
              onChange={setLocationTargets}
            />
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="outline" onClick={closeEditorModal}>
              Cancelar
            </Button>
            <Button
              onClick={() => void handleSaveAlertMessage()}
              disabled={isSaveDisabled}
              loading={isSaving}
            >
              {editingAlert ? 'Guardar cambios' : 'Crear alerta'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AlertsTab;
